import React from 'react';
import {
  QueryObserverResult,
  RefetchOptions,
  UseMutateFunction,
  useQuery,
  useQueryClient,
} from 'react-query';

import { Alert } from 'components/ui/alert';
import { LoadingMessage } from 'components/Loader/LoadingMessage';
import { User } from 'interfaces/user';
import { useLocalStorageState } from './useLocalStorageState';
import { useService } from './useService';
import { ContentType } from 'services/abstract';

export interface AuthContextValue {
  user: User | undefined;
  isAuthenticated: boolean;
  setUser?: (data: User | undefined) => void;
  authenticate?: (user: User, jwt: string) => void;
  logout?: UseMutateFunction;
  refetchUser?: (
    options?: RefetchOptions | undefined
  ) => Promise<QueryObserverResult<User, Error>>;
  error: Error | null;
}

const AuthContext = React.createContext<AuthContextValue>({
  user: undefined,
  isAuthenticated: false,
  error: null,
});
AuthContext.displayName = 'AuthContext';

export interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  const { value: jwtToken, persistValue: persistJwtToken } =
    useLocalStorageState('token');
  const queryClient = useQueryClient();
  const userService = useService(ContentType.USERS);
  const key = 'auth-user';
  const isAuthenticated = !!jwtToken;

  const {
    data: user,
    error,
    isLoading,
    refetch,
  } = useQuery<User, Error>({
    queryKey: key,
    queryFn: async () => {
      return await userService.getCurrentUser(jwtToken);
    },
    enabled: isAuthenticated,
  });

  const setUser = (data: User | undefined) =>
    queryClient.setQueryData(key, data);

  const authenticate = (user: User, jwt: string) => {
    setUser?.(user);
    persistJwtToken(jwt);
  };

  const logout = () => {
    userService.logout();
    persistJwtToken('');
    queryClient.clear();
  };

  if (isLoading) {
    return <LoadingMessage />;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: isAuthenticated,
        error,
        setUser,
        authenticate,
        refetchUser: refetch,
        logout,
      }}
    >
      {error && <Alert variant="destructive">{error.message}</Alert>}
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  return context;
};
